import {
    FC,
    ReactElement,
    useEffect,
} from 'react'
import { useNavigate } from 'react-router'

import { InfoListItem, LoadingSpinner, ZeroState } from '@/components'
import { ExchangeListItem, ShiftExchangeListItem, TabButtons } from '@/compositions'
import { ExchangesTab, MY_EXCHANGES, OFFERED_EXCHANGES } from '@/constants'
import { trans } from '@/helpers/trans'
import { ExchangeResource, ShiftExchangeViewModel } from '@/models'
import {
    getExchangesOfferedByMe,
    getExchangesOfferedForMe,
    getExchangesTabLink,
    getShiftExchanges,
} from '@/redux/exchanges/exchangesActions'
import { useTypedDispatch, useTypedSelector } from '@/redux/store'

import './ExchangesPage.scss'

interface ExchangesPageProps {
    tab: ExchangesTab
}

export const ExchangesPage: FC<ExchangesPageProps> = ({ tab }): ReactElement | null => {
    const dispatch = useTypedDispatch()
    const navigate = useNavigate()
    const { shiftsOfferedByMe, shiftsOfferedForMe, shiftExchanges } = useTypedSelector(state => state.exchangesReducer)
    const { currentUser, settings } = useTypedSelector(state => state.userReducer)
    const { loading } = useTypedSelector(state => state.appReducer)

    useEffect(() => {
        dispatch(getExchangesOfferedByMe())
        dispatch(getExchangesOfferedForMe())
        dispatch(getShiftExchanges())
    }, [])

    if (! currentUser) {
        return null
    }

    const handleTabButtonClick = (index: number): void => {
        const newTab = index === 0 ? MY_EXCHANGES : OFFERED_EXCHANGES

        if (newTab !== tab) {
            navigate(getExchangesTabLink(newTab))
        }
    }

    const navigateToExchange = (exchange: ExchangeResource): void => {
        navigate(`/${trans('nav.exchanges.link')}/${exchange.id}`)
    }

    const navigateToShiftExchange = (shiftExchange: ShiftExchangeViewModel): void => {
        navigate(`/${trans('nav.exchanges.shiftExchange.link')}/${shiftExchange.id}`)
    }

    const renderShiftExchange = (shiftExchange: ShiftExchangeViewModel): ReactElement => (
        <ShiftExchangeListItem
            key={shiftExchange.id}
            settings={settings}
            exchange={shiftExchange}
            onClick={() => navigateToShiftExchange(shiftExchange)}
            userId={currentUser.id}
        />
    )

    const renderExchange = (exchange: ExchangeResource, byMe: boolean): ReactElement => (
        <ExchangeListItem
            key={exchange.id}
            byMe={byMe}
            settings={settings}
            exchange={exchange}
            onClick={() => navigateToExchange(exchange)}
        />
    )

    const renderZeroState = (): ReactElement => (
        <ZeroState
            text={trans('pages.exchanges.noExchangesFound')}
            className="exchanges__zero-state"
        />
    )

    const renderExchangesOfferedByMe = (): ReactElement | ReactElement[] => {
        const shiftExchangesByMe = shiftExchanges
            .filter(shiftExchange => shiftExchange.shiftPlanning1.user?.id === currentUser.id)
        const exchanges = [...shiftsOfferedByMe, ...shiftExchangesByMe]

        if (! exchanges.length) {
            return renderZeroState()
        }

        return exchanges.map(exchange => (
            exchange.type === 'shift-exchanges' ? renderShiftExchange(exchange) : renderExchange(exchange, true)
        ))
    }

    const renderExchangesOfferedForMe = (): ReactElement => {
        const shiftExchangesForMe = shiftExchanges
            .filter(shiftExchange => shiftExchange.shiftPlanning2.user?.id === currentUser.id)

        if (! shiftsOfferedForMe.length && ! shiftExchangesForMe.length) {
            return renderZeroState()
        }

        return (
            <>
                {!! shiftExchangesForMe.length && <InfoListItem label={trans('common.forYou')} />}
                {shiftExchangesForMe.map(renderShiftExchange)}
                {!! shiftsOfferedForMe.length && <InfoListItem label={trans('common.vacant')} />}
                {shiftsOfferedForMe.map(exchange => renderExchange(exchange, false))}
            </>
        )
    }

    return (
        <>
            <TabButtons
                activeIndex={tab === MY_EXCHANGES ? 0 : 1}
                buttonLabels={[
                    trans('pages.exchanges.exchangesOfferedByMe'),
                    trans('pages.exchanges.exchangesOfferedByOthers'),
                ]}
                onButtonClick={handleTabButtonClick}
            />
            <div>
                <div hidden={tab !== MY_EXCHANGES}>
                    <div className="exchanges__row">
                        <div className="exchanges__col">
                            <ul>
                                {renderExchangesOfferedByMe()}
                            </ul>
                        </div>
                    </div>
                </div>
                <div hidden={tab !== OFFERED_EXCHANGES}>
                    <div className="exchanges__row">
                        <div className="exchanges__col">
                            <ul>
                                {renderExchangesOfferedForMe()}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            {loading && <LoadingSpinner />}
        </>
    )
}
